import React from 'react';
import { ArrowDownIcon, CircleDotIcon } from 'lucide-react';
import { MCP_EXAMPLE_STEPS } from '../data/mcp';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { McpWaitlistButton } from '../components/mcp/McpWaitlistButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { ExampleFrame } from '../components/examples/ExampleFrame';
import { McpWaitlistForm } from '../components/mcp/McpWaitlistForm';
function ExampleChain() {
  return <ExampleFrame title="Investment team · new submission" subtitle="Three agents, one draft for review" caption="Illustrative example of how agents could be combined. Not a list of available tools." data-id="element-1899" data-mp="2ogs5faa10b28">
      <ol className="flex flex-col" data-id="element-1900" data-mp="3pi8460a8ws1">
        {MCP_EXAMPLE_STEPS.map((step, index) => <li key={step.agent} className="flex flex-col" data-id="element-1901" data-mp="3vu3yqblt52mm" data-mpi={index}>
            <div className="rounded-xl border border-hairline-soft bg-surface px-3.5 py-3" data-id="element-1902" data-mp="3vtkir0fpwob8">
              <div className="flex items-center justify-between gap-2" data-id="element-1903" data-mp="3gz4a1qy03wjx">
                <p className="text-sm font-semibold text-text-primary" data-id="element-1904" data-mp="3lxnufmwy50a6">{step.action}</p>
                <span className="font-mono text-[11px] text-lime" data-id="element-1905" data-mp="3q02c43ovptzv">{step.agent}</span>
              </div>
              <p className="mt-1 text-[13px] text-text-secondary" data-id="element-1906" data-mp="3uzndekriuefr">{step.result}</p>
            </div>
            {index < MCP_EXAMPLE_STEPS.length - 1 ? <span className="flex h-6 items-center justify-center text-text-tertiary" aria-hidden="true" data-id="element-1907" data-mp="2y7beppvglz91">
                <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-1908" data-mp="379t247muoxxm" />
              </span> : null}
          </li>)}
      </ol>
      <p className="mt-4 rounded-lg bg-violet-soft px-3.5 py-2.5 text-[13px] font-medium text-lime" data-id="element-1909" data-mp="3c0suczl0yrts">An analyst reviews the draft before anything moves forward.</p>
    </ExampleFrame>;
}
export function Mcp() {
  usePageMeta({
    title: 'MCP Access Waitlist | DiligenceGPT™ Agents in Your Own Workflow',
    description: 'DiligenceGPT™ has set up a Model Context Protocol (MCP) server for access to its due diligence agents. Join the waitlist and tell us which workflow you want to connect first.',
    path: '/mcp',
    keywords: ['DiligenceGPT MCP', 'Model Context Protocol due diligence', 'due diligence AI agents API'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'MCP Access',
        item: canonicalUrl('/mcp')
      }]
    }]
  });
  return <main id="main" data-id="element-1910" data-mp="3cuve3uf6if0p">
      <PageHero headingId="mcp-heading" breadcrumb="MCP Access" eyebrow="Developer access · Waitlist open" title="Use DiligenceGPT™ agents in your own workflow." description="We’ve set up a Model Context Protocol (MCP) server for access to DiligenceGPT™ agents. Join the waitlist and tell us which diligence workflow you want to connect first." actions={<McpWaitlistButton placement="mcp_hero" size="xl" className="w-full sm:w-auto" data-id="element-1912" data-mp="vr107l74ko64" />} aside={<div className="flex flex-col gap-3" data-id="element-1913" data-mp="lqh0imz97rq0">
            <div className="flex items-center gap-3 rounded-xl border border-night-line bg-night-panel px-4 py-3" data-id="element-1914" data-mp="lrsa53fdrtp1">
              <CircleDotIcon className="h-4 w-4 shrink-0 text-[#4ADE80]" aria-hidden="true" data-id="element-1915" data-mp="qptudrojlz23" />
              <p className="text-sm text-night-text" data-id="element-1916" data-mp="1efxys19k22u5">
                <span className="font-semibold" data-id="element-1917" data-mp="1jer0kwfhc5eh">Status: Waitlist open.</span> <span className="text-night-muted" data-id="element-1918" data-mp="1jgbzey8tuai5">Access isn’t available yet.</span>
              </p>
            </div>
            <ExampleChain data-id="element-1919" data-mp="19ofub38bpk4f" />
          </div>} data-id="element-1911" data-mp="37xv79hai988j" />

      <section id="waitlist" aria-labelledby="waitlist-heading" className="scroll-mt-24 border-b border-hairline-soft bg-surface" data-id="element-1920" data-mp="19muzdci3jz61">
        <div className="mx-auto grid w-full max-w-[1100px] gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14 lg:px-8 lg:py-24" data-id="element-1921" data-mp="224qlr88790cm">
          <div data-id="element-1922" data-mp="dkh4xerpaj9n">
            <SectionHeading eyebrow="Waitlist" headingId="waitlist-heading" title="Join the MCP access waitlist." description="Leave your details and we’ll email you when access is available." data-id="element-1923" data-mp="dgtamdeflstv" />
            <Reveal as="ul" className="mt-8 flex flex-col gap-3 text-[15px] text-text-secondary" data-id="element-1924" data-mp="3vpkepcsm3jge">
              <li className="flex gap-3" data-id="element-1925" data-mp="x5vdu0lyvahb">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-1926" data-mp="x8pxhpx5or9o" />
                For investment, procurement, acquisition, selection, risk and monitoring workflows.
              </li>
              <li className="flex gap-3" data-id="element-1927" data-mp="iiaaq6p3db9e">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-1928" data-mp="ih7mk6j5lmx6" />
                Agents prepare the work; your team still reviews and decides.
              </li>
            </Reveal>
          </div>
          <Reveal delay={0.05} data-id="element-1929" data-mp="1b4ldno067o9d">
            <McpWaitlistForm idPrefix="mcp-page" data-id="element-1930" data-mp="1iicefken62nj" />
          </Reveal>
        </div>
      </section>
    </main>;
}